import { useState, useEffect, useCallback } from 'react';
import { LinkingState } from '../types';
import { aoUtils, isValidEvmAddress } from '../utils/ao';

export const useArConnect = () => {
  const [state, setState] = useState<LinkingState>({
    isConnected: false,
    arweaveAddress: null,
    evmAddress: null,
    isLoading: false,
    error: null,
  });

  const [walletCheckComplete, setWalletCheckComplete] = useState(false);
  const [isWalletAvailable, setIsWalletAvailable] = useState(false);

  // Check if ArConnect is available
  const isArConnectAvailable = useCallback(() => {
    return typeof window !== 'undefined' && window.arweaveWallet;
  }, []);

  // Connect to ArConnect wallet
  const connectWallet = useCallback(async () => {
    if (!isArConnectAvailable()) {
      setState(prev => ({ ...prev, error: 'ArConnect wallet not found. Please install ArConnect extension.' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await window.arweaveWallet.connect(['ACCESS_ADDRESS', 'SIGN_TRANSACTION']);
      const address = await window.arweaveWallet.getActiveAddress();
      
      setState(prev => ({ 
        ...prev, 
        isConnected: true, 
        arweaveAddress: address,
        isLoading: false 
      }));

      // Check for existing linked address
      await checkExistingLink();
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to connect to ArConnect wallet',
        isLoading: false 
      }));
    }
  }, [isArConnectAvailable]);

  // Disconnect wallet
  const disconnectWallet = useCallback(async () => {
    if (!isArConnectAvailable()) return;

    try {
      await window.arweaveWallet.disconnect();
      setState({
        isConnected: false,
        arweaveAddress: null,
        evmAddress: null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Failed to disconnect wallet:', error);
    }
  }, [isArConnectAvailable]);

  // Check for existing linked EVM address
  const checkExistingLink = useCallback(async () => {
    if (!state.isConnected) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await aoUtils.getLinkedAddress();
      
      if (result.address) {
        setState(prev => ({ 
          ...prev, 
          evmAddress: result.address || null,
          isLoading: false 
        }));
      } else if (result.error && result.error !== 'No EVM address linked') {
        setState(prev => ({ 
          ...prev, 
          error: result.error || null,
          isLoading: false 
        }));
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Failed to check existing link:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to check existing linked address',
        isLoading: false 
      }));
    }
  }, [state.isConnected]);

  // Link EVM address
  const linkEvmAddress = useCallback(async (evmAddress: string) => {
    if (!state.isConnected) {
      setState(prev => ({ ...prev, error: 'Please connect your ArConnect wallet first' }));
      return false;
    }

    if (!isValidEvmAddress(evmAddress)) {
      setState(prev => ({ ...prev, error: 'Invalid EVM address format' }));
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await aoUtils.linkEvmAddress(evmAddress);
      
      if (result.success) {
        setState(prev => ({ 
          ...prev, 
          evmAddress: evmAddress,
          isLoading: false 
        }));
        return true;
      } else {
        setState(prev => ({ 
          ...prev, 
          error: result.error || 'Failed to link EVM address',
          isLoading: false 
        }));
        return false;
      }
    } catch (error) {
      console.error('Failed to link EVM address:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to link EVM address',
        isLoading: false 
      }));
      return false;
    }
  }, [state.isConnected]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Check wallet availability periodically
  useEffect(() => {
    let mounted = true;
    let intervalId: NodeJS.Timeout;
    
    const checkWalletAvailability = () => {
      const available = isArConnectAvailable();
      const isAvailable = !!available;
      if (mounted) {
        setIsWalletAvailable(isAvailable);
      }
      
      // Stop checking once wallet is found
      if (isAvailable && intervalId) {
        clearInterval(intervalId);
      }
    };

    // Check immediately
    checkWalletAvailability();
    
    // Check every 500ms until wallet is found
    if (!isWalletAvailable) {
      intervalId = setInterval(checkWalletAvailability, 500);
    }

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isArConnectAvailable, isWalletAvailable]);

  // Check wallet connection on mount and when wallet becomes available
  useEffect(() => {
    let mounted = true;
    
    const checkConnection = async () => {
      if (!isWalletAvailable) {
        if (mounted) setWalletCheckComplete(true);
        return;
      }

      try {
        const permissions = await window.arweaveWallet.getPermissions();
        
        if (permissions.includes('ACCESS_ADDRESS')) {
          const address = await window.arweaveWallet.getActiveAddress();
          
          if (mounted) {
            setState(prev => ({ 
              ...prev, 
              isConnected: true, 
              arweaveAddress: address 
            }));
            
            // Check for existing linked address
            setTimeout(checkExistingLink, 100);
          }
        }
      } catch (error) {
        // Wallet not connected or permission denied - this is expected
      }
      
      if (mounted) setWalletCheckComplete(true);
    };

    if (isWalletAvailable) {
      checkConnection();
    }

    return () => {
      mounted = false;
    };
  }, [isWalletAvailable, checkExistingLink]);

  return {
    ...state,
    isArConnectAvailable: isWalletAvailable,
    connectWallet,
    disconnectWallet,
    linkEvmAddress,
    checkExistingLink,
    clearError,
  };
};
